import React, { useState, useEffect } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { 
  Form, Input, Button, DatePicker, InputNumber, Select, 
  message, Spin, Slider, Switch, Tag, Row, Col 
} from "antd";
import useLocationOptions from "../../../hooks/useLocationOptions";
import { 
  ArrowLeftOutlined, PlusOutlined, RightOutlined, LeftOutlined,
  ProjectOutlined, LockOutlined, RobotOutlined, FormOutlined,
  FileSearchOutlined, TeamOutlined
} from "@ant-design/icons";
import styled from "styled-components";
import axios from "axios";
import { API_CONST } from "../../../const";
import dayjs from "dayjs";

const { TextArea } = Input;
const { Option } = Select;
const { RangePicker } = DatePicker;

const TECHNOLOGY_OPTIONS = [
  "React", "Angular", "Vue.js", "Next.js", "Node.js", "Express.js",
  "Python", "Django", "Flask", "FastAPI", "Java", "Spring Boot",
  "Go", "Rust", "Ruby", "Rails", "PHP", "Laravel",
  "TypeScript", "JavaScript", "C#", ".NET", "AWS", "Azure", "GCP",
  "Docker", "Kubernetes", "MongoDB", "PostgreSQL", "MySQL", "Redis",
  "GraphQL", "REST API", "Microservices", "Serverless", "Machine Learning",
  "AI/ML", "Data Engineering", "DevOps", "CI/CD", "Terraform"
];

const ROLE_OPTIONS = [
  "Frontend Developer", "Backend Developer", "Full Stack Developer",
  "DevOps Engineer", "Cloud Architect", "Data Engineer", "Data Scientist",
  "ML Engineer", "QA Engineer", "Security Engineer", "Tech Lead",
  "Solution Architect", "Mobile Developer", "UI/UX Designer", "Product Manager"
];

const FormWrapper = styled.div`
  height: 100vh;
  background: linear-gradient(135deg, #f5f7fa 0%, #e4e8eb 100%);
  padding: 60px 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;

  .form-card {
    max-width: 1100px;
    width: 100%;
    margin: 0 auto;
    background: white;
    border-radius: 16px;
    box-shadow: 0 8px 40px rgba(0, 0, 0, 0.08);
    display: flex;
    flex-direction: column;
    height: calc(100vh - 120px);
  }

  .progress-section {
    padding: 24px 32px;
    border-bottom: 1px solid #f0f0f0;
    flex-shrink: 0;
    background: white;

    .progress-bar {
      display: flex;
      align-items: center;
      justify-content: center;
      margin-bottom: 16px;
    }

    .progress-step {
      display: flex;
      align-items: center;
    }

    .step-dot {
      width: 12px;
      height: 12px;
      border-radius: 50%;
      background: #e2e8f0;
      transition: all 0.3s ease;

      &.active {
        background: #01d9a9;
        box-shadow: 0 0 0 4px rgba(1, 217, 169, 0.2);
      }

      &.completed {
        background: #014c75;
      }
    }

    .step-line {
      width: 80px;
      height: 3px;
      background: #e2e8f0;
      margin: 0 4px;

      &.completed {
        background: #014c75;
      }
    }

    .step-info {
      text-align: center;

      .step-label {
        font-size: 14px;
        color: #94a3b8;
        margin-bottom: 4px;
      }

      .step-title {
        font-size: 24px;
        font-weight: 700;
        color: #014c75;
        margin: 0;
      }

      .step-description {
        font-size: 15px;
        color: #64748b;
        margin-top: 4px;
      }
    }
  }

  .card-content {
    padding: 40px 56px 48px;
    flex: 1;
    overflow-y: auto;

    @media (max-width: 768px) {
      padding: 32px 24px 40px;
    }
  }

  .card-footer {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 20px 32px;
    border-top: 1px solid #e8e8e8;
    background: #fafafa;
    border-radius: 0 0 16px 16px;
    flex-shrink: 0;

    .cancel-btn {
      height: 44px;
      padding: 0 24px;
      border-radius: 10px;
      font-weight: 500;
      color: #64748b;
      border: 1px solid #e2e8f0;
      background: white;

      &:hover {
        border-color: #014c75;
        color: #014c75;
      }
    }

    .nav-buttons {
      display: flex;
      gap: 12px;
    }

    .back-btn {
      height: 44px;
      padding: 0 24px;
      border-radius: 10px;
      font-weight: 500;
      border: 1px solid #e2e8f0;
      background: white;
      color: #64748b;

      &:hover {
        border-color: #014c75;
        color: #014c75;
      }
    }

    .next-btn {
      height: 44px;
      padding: 0 32px;
      border-radius: 10px;
      font-weight: 600;
      background: linear-gradient(135deg, #01d9a9 0%, #00b894 100%);
      border: none;
      color: white;
      box-shadow: 0 4px 12px rgba(1, 217, 169, 0.3);

      &:hover {
        background: linear-gradient(135deg, #00e8b5 0%, #01d9a9 100%);
        box-shadow: 0 6px 16px rgba(1, 217, 169, 0.4);
      }
    }

    .submit-btn {
      height: 44px;
      padding: 0 32px;
      border-radius: 10px;
      font-weight: 600;
      background: linear-gradient(135deg, #014c75 0%, #013a5c 100%);
      border: none;
      color: white;
      box-shadow: 0 4px 12px rgba(1, 76, 117, 0.3);

      &:hover {
        box-shadow: 0 6px 16px rgba(1, 76, 117, 0.4);
      }
    }
  }

  .form-section-title {
    font-size: 18px;
    font-weight: 600;
    color: #014c75;
    margin-bottom: 24px;
    padding-bottom: 12px;
    border-bottom: 2px solid #f1f5f9;
  }

  .ant-form-item {
    margin-bottom: 24px;

    .ant-form-item-label > label {
      font-size: 15px;
      font-weight: 600;
      color: #334155;
    }

    .ant-input,
    .ant-select-selector,
    .ant-picker,
    .ant-input-number {
      border-radius: 10px !important;
      border: 2px solid #e2e8f0 !important;
      font-size: 15px !important;
      min-height: 44px;

      &:hover {
        border-color: #01d9a9 !important;
      }

      &:focus,
      &.ant-select-focused {
        border-color: #01d9a9 !important;
        box-shadow: 0 0 0 3px rgba(1, 217, 169, 0.1) !important;
      }
    }

    .ant-input-textarea textarea {
      padding: 12px 16px !important;
      min-height: 100px;
    }
  }

  .slider-wrapper {
    padding: 16px 24px;
    background: #f8fafc;
    border-radius: 12px;
    margin-top: 8px;

    .slider-labels {
      display: flex;
      justify-content: space-between;
      margin-bottom: 8px;
      font-size: 13px;
      color: #64748b;
    }

    .slider-value {
      text-align: center;
      font-size: 20px;
      font-weight: 700;
      color: #014c75;
      margin-bottom: 12px;
    }

    .ant-slider {
      margin: 0;

      .ant-slider-track {
        background: linear-gradient(90deg, #01d9a9, #014c75);
        height: 6px;
      }

      .ant-slider-rail {
        background: #e2e8f0;
        height: 6px;
      }

      .ant-slider-handle {
        width: 20px !important;
        height: 20px !important;
        border: 3px solid #014c75 !important;
        background: white !important;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15) !important;
        margin-top: -7px !important;
        border-radius: 50% !important;
        inset-block-start: 0 !important;

        &:hover,
        &:focus {
          border-color: #01d9a9 !important;
        }

        &::before, &::after {
          display: none !important;
        }
      }
    }
  }

  .custom-tech-input {
    display: flex;
    gap: 12px;
    margin-top: 12px;

    .ant-input {
      flex: 1;
    }

    .add-tech-btn {
      height: 44px;
      padding: 0 20px;
      border-radius: 10px;
      background: #f1f5f9;
      border: 2px dashed #cbd5e1;
      color: #64748b;

      &:hover {
        background: #e8f7f5;
        border-color: #01d9a9;
        color: #01d9a9;
      }
    }
  }

  .option-cards {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 12px;
    margin-top: 8px;

    .option-card {
      padding: 16px;
      border: 2px solid #e2e8f0;
      border-radius: 12px;
      text-align: center;
      cursor: pointer;
      transition: all 0.2s ease;
      background: white;

      &:hover {
        border-color: #01d9a9;
        background: #f8fffd;
      }

      &.selected {
        border-color: #01d9a9;
        background: linear-gradient(135deg, #e8f7f5 0%, #d1f4ed 100%);

        .option-label {
          color: #014c75;
        }
      }

      .option-icon {
        font-size: 28px;
        margin-bottom: 8px;
      }

      .option-label {
        font-size: 14px;
        font-weight: 600;
        color: #64748b;
      }
    }
  }

  .summary-card {
    background: linear-gradient(135deg, #f8fafc 0%, #e8f4f8 100%);
    padding: 24px;
    border-radius: 16px;
    border: 1px solid #e2e8f0;
    margin-top: 16px;

    .summary-row {
      margin-bottom: 16px;

      &:last-child {
        margin-bottom: 0;
      }

      .summary-label {
        font-size: 13px;
        color: #64748b;
        margin-bottom: 4px;
      }

      .summary-value {
        font-size: 15px;
        font-weight: 600;
        color: #014c75;
        text-transform: capitalize;
      }
    }
  }
`;

const WelcomeWrapper = styled.div`
  min-height: 100vh;
  background: linear-gradient(135deg, #f5f7fa 0%, #e4e8eb 100%);
  padding: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow-y: auto;

  .welcome-card {
    max-width: 1100px;
    width: 100%;
    margin: 0 auto;
    background: white;
    border-radius: 16px;
    box-shadow: 0 8px 40px rgba(0, 0, 0, 0.08);
    padding: 48px 64px;
    text-align: center;

    @media (max-width: 768px) {
      padding: 32px 24px;
    }
  }

  .welcome-header {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 20px;
    margin-bottom: 12px;
  }

  .welcome-icon {
    width: 56px;
    height: 56px;
    background: linear-gradient(135deg, #01d9a9 0%, #014c75 100%);
    border-radius: 14px;
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 8px 24px rgba(1, 217, 169, 0.3);
    flex-shrink: 0;

    svg {
      font-size: 28px;
      color: white;
    }
  }

  .welcome-title {
    font-size: 28px;
    font-weight: 700;
    color: #014c75;
    margin: 0;
  }

  .welcome-subtitle {
    font-size: 16px;
    color: #64748b;
    max-width: 600px;
    margin: 0 auto 28px;
    line-height: 1.5;
  }

  .creation-options {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 20px;
    justify-content: center;
    margin-bottom: 24px;
    max-width: 1000px;
    margin-left: auto;
    margin-right: auto;

    @media (max-width: 900px) {
      grid-template-columns: 1fr;
      max-width: 420px;
    }

    .option-card {
      background: white;
      border-radius: 14px;
      padding: 24px;
      cursor: pointer;
      border: 2px solid #e8e8e8;
      transition: all 0.3s ease;
      text-align: left;
      display: flex;
      flex-direction: column;

      &:hover {
        border-color: #01d9a9;
        transform: translateY(-2px);
        box-shadow: 0 8px 24px rgba(1, 217, 169, 0.15);
      }

      .card-header {
        display: flex;
        flex-direction: row;
        align-items: center;
        gap: 12px;
        margin-bottom: 14px;
        flex-wrap: wrap;
      }

      .option-icon {
        width: 44px;
        height: 44px;
        border-radius: 11px;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 22px;
        flex-shrink: 0;
      }

      .option-title {
        font-size: 16px;
        font-weight: 700;
        color: #014c75;
        margin: 0;
        flex: 1;
        min-width: 0;
      }

      .option-description {
        font-size: 13px;
        color: #64748b;
        line-height: 1.6;
        margin: 0 0 auto 0;
        flex: 1;
      }

      .option-badge {
        font-size: 10px;
        padding: 3px 10px;
        border-radius: 20px;
        font-weight: 600;
        white-space: nowrap;
        align-self: flex-start;
        margin-bottom: 12px;

        &.greenfield {
          background: linear-gradient(135deg, #01d9a9 0%, #00b894 100%);
          color: white;
        }

        &.brownfield {
          background: linear-gradient(135deg, #8c5e00 0%, #6b4700 100%);
          color: white;
        }

        &.managed {
          background: linear-gradient(135deg, #0050b3 0%, #003a8c 100%);
          color: white;
        }
      }

      .option-helper {
        font-size: 11px;
        color: #94a3b8;
        font-style: italic;
        margin-top: 14px;
        padding-top: 10px;
        border-top: 1px solid #f0f0f0;
        line-height: 1.4;
      }

      &.ai-option .option-icon {
        background: linear-gradient(135deg, #01d9a9 0%, #00b894 100%);
        color: white;
      }

      &.manual-option .option-icon {
        background: linear-gradient(135deg, #e8f4f8 0%, #d1ecf4 100%);
        color: #014c75;
      }

      &.staff-aug-option .option-icon {
        background: linear-gradient(135deg, #e6f7ff 0%, #bae7ff 100%);
        color: #0050b3;
      }
    }
  }

  .privacy-note {
    padding: 12px 20px;
    background: rgba(1, 76, 117, 0.04);
    border-radius: 10px;
    font-size: 12px;
    color: #64748b;
    max-width: 600px;
    margin: 0 auto;
    border: 1px solid rgba(1, 76, 117, 0.1);
  }

  .existing-assessment-link {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    padding: 12px 24px;
    background: linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%);
    border: 1px dashed #0284c7;
    border-radius: 10px;
    margin-bottom: 28px;
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover {
      background: linear-gradient(135deg, #e0f2fe 0%, #bae6fd 100%);
      border-color: #0369a1;
      transform: translateY(-1px);
    }

    .link-icon {
      font-size: 18px;
      color: #0284c7;
    }

    .link-text {
      font-size: 14px;
      color: #0369a1;
      font-weight: 500;
    }

    .link-arrow {
      font-size: 12px;
      color: #0284c7;
    }
  }
`;

const CreateProject = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const preselectedEngagementId = searchParams.get("engagementId");
  const isEdit = !!id;
  const [form] = Form.useForm();
  const watchedPrimaryTech = Form.useWatch("primaryTechnologies", form);
  const primaryTechCount = (watchedPrimaryTech || []).length;
  const [loading, setLoading] = useState(false);
  const [fetchLoading, setFetchLoading] = useState(false);
  const [activeEngagements, setActiveEngagements] = useState([]);
  const [currentStep, setCurrentStep] = useState(0);
  const [showWelcome, setShowWelcome] = useState(!isEdit);
  const [creationMode, setCreationMode] = useState(null);
  const [customTech, setCustomTech] = useState("");
  const [dataSensitivity, setDataSensitivity] = useState(false);
  const [complexityLevel, setComplexityLevel] = useState(50);
  const [maturityIndex, setMaturityIndex] = useState(3);
  const [selectedEngagement, setSelectedEngagement] = useState(null);
  const { countries, cities, fetchCities } = useLocationOptions(null);

  const steps = [
    { title: "Project Basics", description: "Name, requirements & timeline" },
    { title: "Technical Requirements", description: "Technologies & architecture" },
    { title: "Complexity & Maturity", description: "Project metrics & security" },
    { title: "Team Expectations", description: "Team size & critical roles" },
  ];

  const staffAugSteps = [
    { title: "Resource Basics", description: "Name, objective & timeline" },
    { title: "Team & Roles", description: "Team size & resource requirements" },
  ];

  useEffect(() => {
    fetchActiveEngagements();
    if (isEdit) {
      fetchProject();
    } else if (preselectedEngagementId) {
      form.setFieldsValue({ engagementId: preselectedEngagementId });
    }
  }, [id, preselectedEngagementId]);

  useEffect(() => {
    if (preselectedEngagementId && activeEngagements.length > 0) {
      const eng = activeEngagements.find(e => e.id === preselectedEngagementId);
      if (eng) setSelectedEngagement(eng);
    }
  }, [preselectedEngagementId, activeEngagements]);

  const fetchActiveEngagements = async () => {
    try {
      const token = localStorage.getItem("authToken");
      const response = await axios.post(
        API_CONST.GET_ACTIVE_ENGAGEMENTS,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setActiveEngagements(response.data.engagements || []);
    } catch (error) {
      console.error("Error fetching engagements:", error);
    }
  };

  const fetchProject = async () => {
    setFetchLoading(true);
    try {
      const token = localStorage.getItem("authToken");
      const response = await axios.get(
        `${API_CONST.GET_PROJECT}/${id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const project = response.data.project;

      if (project.status !== "pending_approval" && project.status !== "rejected") {
        message.error("Only pending or rejected projects can be edited");
        navigate("/customer/projects");
        return;
      }

      form.setFieldsValue({
        engagementId: project.engagementId,
        title: project.title,
        projectObjective: project.projectObjective || project.description,
        tentativeStartDate: project.expectedStartDate ? dayjs(project.expectedStartDate) : null,
        durationMonths: project.durationMonths || null,
        deliveryType: project.deliveryType,
        budgetAllocated: project.budgetAllocated,
        primaryTechnologies: project.primaryTechnologies || [],
        secondaryTechnologies: project.secondaryTechnologies || [],
        architectureType: project.architectureType,
        integrationComplexity: project.integrationComplexity,
        complexityLevel: project.complexityLevel || 50,
        maturityIndex: project.maturityIndex || 3,
        dataSensitivity: project.dataSensitivity || false,
        expectedTeamSize: project.expectedTeamSize,
        criticalRoles: project.criticalRoles || [],
        experienceLevel: project.experienceLevel,
        country: project.country || null,
        preferredLocations: project.preferredLocations || [],
      });
      if (project.country) fetchCities(project.country);
      setDataSensitivity(project.dataSensitivity || false);
      setComplexityLevel(project.complexityLevel || 50);
      setMaturityIndex(project.maturityIndex || 3);
      if (project.creationMode) {
        setCreationMode(project.creationMode);
      }
    } catch (error) {
      console.error("Error fetching project:", error);
      message.error("Failed to load project");
      navigate("/customer/projects");
    } finally {
      setFetchLoading(false);
    }
  };

  const handleSubmit = async () => {
    try {
      await form.validateFields();
      const values = form.getFieldsValue(true);
      setLoading(true);
      const token = localStorage.getItem("authToken");

      const activeCreationMode = creationMode || "MANUAL";
      
      let payload;
      if (activeCreationMode === "STAFF_AUGMENTATION") {
        payload = {
          engagementId: values.engagementId,
          title: values.title,
          description: values.projectObjective,
          projectObjective: values.projectObjective,
          expectedStartDate: values.tentativeStartDate?.format("YYYY-MM-DD"),
          durationMonths: values.durationMonths,
          budgetAllocated: values.budgetAllocated,
          creationMode: "STAFF_AUGMENTATION",
          expectedTeamSize: values.expectedTeamSize,
          criticalRoles: values.criticalRoles,
          experienceLevel: values.experienceLevel,
          country: values.country || undefined,
          preferredLocations: values.preferredLocations,
          primaryTechnologies: values.primaryTechnologies || [],
          secondaryTechnologies: values.secondaryTechnologies || [],
        };
      } else {
        payload = {
          engagementId: values.engagementId,
          title: values.title,
          description: values.projectObjective,
          expectedStartDate: values.timeline?.[0]?.format("YYYY-MM-DD"),
          expectedEndDate: values.timeline?.[1]?.format("YYYY-MM-DD"),
          budgetAllocated: values.budgetAllocated,
          creationMode: "MANUAL",
          projectObjective: values.projectObjective,
          deliveryType: values.deliveryType,
          primaryTechnologies: values.primaryTechnologies,
          secondaryTechnologies: values.secondaryTechnologies,
          architectureType: values.architectureType,
          integrationComplexity: values.integrationComplexity,
          complexityLevel: complexityLevel,
          maturityIndex: maturityIndex,
          dataSensitivity: dataSensitivity,
          expectedTeamSize: values.expectedTeamSize,
          criticalRoles: values.criticalRoles,
        };
      }

      if (isEdit) {
        await axios.put(
          `${API_CONST.UPDATE_PROJECT}/${id}`,
          payload,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        message.success("Project updated successfully");
        navigate("/customer/projects");
      } else {
        const createResponse = await axios.post(
          API_CONST.CREATE_PROJECT,
          payload,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        message.success("Project created successfully. Waiting for admin approval.");
        const createdProjectId = createResponse.data?.project?.id;
        if (activeCreationMode === "STAFF_AUGMENTATION" && createdProjectId) {
          navigate(`/customer/project/${createdProjectId}`);
        } else {
          navigate("/customer/projects");
        }
      }
    } catch (error) {
      console.error("Error saving project:", error);
      message.error(error.response?.data?.message || "Failed to save project");
    } finally {
      setLoading(false);
    }
  };

  const validateCurrentStep = async () => {
    const activeCreationMode = creationMode || "MANUAL";
    const manualStepFields = {
      0: ["engagementId", "title", "projectObjective", "deliveryType"],
      1: ["primaryTechnologies", "architectureType", "integrationComplexity"],
      2: ["complexityLevel", "maturityIndex"],
      3: [],
    };
    const staffAugStepFields = {
      0: ["engagementId", "title", "projectObjective", "budgetAllocated"],
      1: [],
    };
    const stepFields = activeCreationMode === "STAFF_AUGMENTATION" ? staffAugStepFields : manualStepFields;

    try {
      await form.validateFields(stepFields[currentStep] || []);
      return true;
    } catch (error) {
      return false;
    }
  };

  const handleNext = async () => {
    const isValid = await validateCurrentStep();
    if (isValid) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrev = () => {
    setCurrentStep(currentStep - 1);
  };

  const addCustomTechnology = () => {
    if (customTech && !TECHNOLOGY_OPTIONS.includes(customTech)) {
      const currentPrimary = form.getFieldValue("primaryTechnologies") || [];
      if (creationMode === "STAFF_AUGMENTATION" && currentPrimary.length >= 3) {
        message.warning("Staff Augmentation allows up to 3 primary technologies");
        return;
      }
      form.setFieldsValue({
        primaryTechnologies: [...currentPrimary, customTech]
      });
      setCustomTech("");
    }
  };

  const handleStartSurvey = () => {
    setShowWelcome(false);
  };

  const complexityMarks = {
    0: "Simple",
    25: "Low",
    50: "Medium",
    75: "High",
    100: "Complex",
  };

  const maturityMarks = {
    1: "1",
    2: "2",
    3: "3",
    4: "4",
    5: "5",
  };

  if (fetchLoading) {
    return (
      <FormWrapper>
        <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%" }}>
          <Spin size="large" />
        </div>
      </FormWrapper>
    );
  }

  if (showWelcome && !isEdit) {
    return (
      <WelcomeWrapper>
        <div className="welcome-card">
          <div className="welcome-header">
            <div className="welcome-icon">
              <ProjectOutlined />
            </div>
            <h1 className="welcome-title">Create Your Project</h1>
          </div>
          <p className="welcome-subtitle">
            Choose how you'd like to set up your project requirements.
          </p>

          <div className="creation-options">
            <div 
              className="option-card staff-aug-option"
              onClick={() => { setCreationMode("STAFF_AUGMENTATION"); setShowWelcome(false); setCurrentStep(0); }}
            >
              <div className="card-header">
                <div className="option-icon">
                  <TeamOutlined />
                </div>
                <h3 className="option-title">Staff Augmentation</h3>
              </div>
              <span className="option-badge managed">Managed Service</span>
              <p className="option-description">
                Quickly add skilled resources to your team. Specify roles, team size, experience level, and preferred locations — we match and place the right talent.
              </p>
              <p className="option-helper">We source, vet, and place resources for you — fully managed.</p>
            </div>

            <div 
              className="option-card ai-option"
              onClick={() => navigate(`/customer/create-project/ai-assessment${preselectedEngagementId ? `?engagementId=${preselectedEngagementId}` : ''}`)}
            >
              <div className="card-header">
                <div className="option-icon">
                  <RobotOutlined />
                </div>
                <h3 className="option-title">AI-Assisted Assessment</h3>
              </div>
              <span className="option-badge greenfield">Greenfield</span>
              <p className="option-description">
                Answer a few questions and let AI recommend team structure, budget range, roles, and technologies tailored to your project.
              </p>
              <p className="option-helper">Start fresh with AI-guided requirements, role planning, and budget estimates.</p>
            </div>
            
            <div 
              className="option-card manual-option"
              onClick={() => { setCreationMode("MANUAL"); handleStartSurvey(); }}
            >
              <div className="card-header">
                <div className="option-icon">
                  <FormOutlined />
                </div>
                <h3 className="option-title">Traditional Manual Form</h3>
              </div>
              <span className="option-badge brownfield">Brownfield</span>
              <p className="option-description">
                Fill out a detailed questionnaire to define your project requirements step by step with full control over every input.
              </p>
              <p className="option-helper">Define requirements manually for an existing or enhancement project.</p>
            </div>
          </div>

          <div 
            className="existing-assessment-link"
            onClick={() => navigate(`/customer/create-project/from-assessment${preselectedEngagementId ? `?engagementId=${preselectedEngagementId}` : ''}`)}
          >
            <FileSearchOutlined className="link-icon" />
            <span className="link-text">Or create a project from an existing assessment</span>
            <RightOutlined className="link-arrow" />
          </div>

          <div className="privacy-note">
            <LockOutlined style={{ marginRight: 8 }} />
            Your project details help us match you with the right talent. 
            All information is kept confidential within your organization.
          </div>
        </div>
      </WelcomeWrapper>
    );
  }

  const activeSteps = creationMode === "STAFF_AUGMENTATION" ? staffAugSteps : steps;

  return (
    <FormWrapper>
      <div className="form-card">
        <div className="progress-section">
          <div className="progress-bar">
            {activeSteps.map((step, index) => (
              <div className="progress-step" key={index}>
                <div 
                  className={`step-dot ${
                    index === currentStep ? "active" : 
                    index < currentStep ? "completed" : ""
                  }`} 
                />
                {index < activeSteps.length - 1 && (
                  <div className={`step-line ${index < currentStep ? "completed" : ""}`} />
                )}
              </div>
            ))}
          </div>
          <div className="step-info">
            <div className="step-label">Step {currentStep + 1} of {activeSteps.length}</div>
            <h2 className="step-title">{activeSteps[currentStep].title}</h2>
            <p className="step-description">{activeSteps[currentStep].description}</p>
          </div>
        </div>

        <div className="card-content">
          <Form
            form={form}
            layout="vertical"
            initialValues={{
              complexityLevel: 50,
              maturityIndex: 3,
              dataSensitivity: false,
              primaryTechnologies: [],
              secondaryTechnologies: [],
              criticalRoles: [],
            }}
          >
            {creationMode === "STAFF_AUGMENTATION" && currentStep === 0 && (
              <div className="step-content">
                <Form.Item
                  name="engagementId"
                  label="Select Engagement"
                  rules={[{ required: true, message: "Please select an engagement" }]}
                >
                  <Select
                    placeholder="Choose the engagement for this resource request"
                    disabled={isEdit || !!preselectedEngagementId}
                    size="large"
                    onChange={(value) => {
                      const eng = activeEngagements.find(e => e.id === value);
                      setSelectedEngagement(eng);
                    }}
                  >
                    {activeEngagements.map((eng) => (
                      <Option key={eng.id} value={eng.id}>
                        {eng.title} ({eng.id})
                      </Option>
                    ))}
                  </Select>
                </Form.Item>

                <Form.Item
                  name="title"
                  label="Request Title"
                  rules={[{ required: true, message: "Please enter a title for this resource request" }]}
                >
                  <Input placeholder="e.g., Frontend Developer Augmentation" size="large" />
                </Form.Item>

                <Form.Item
                  name="projectObjective"
                  label="Resource Requirement Description"
                  rules={[{ required: true, message: "Please describe the resource requirement" }]}
                >
                  <TextArea
                    rows={4}
                    placeholder="Describe what skills and experience you need. What will the augmented resources work on?"
                    size="large"
                  />
                </Form.Item>

                <Row gutter={24}>
                  <Col span={8}>
                    <Form.Item name="tentativeStartDate" label="Tentative Start Date">
                      <DatePicker style={{ width: "100%" }} size="large" placeholder="Select start date" />
                    </Form.Item>
                  </Col>
                  <Col span={8}>
                    <Form.Item name="durationMonths" label="Duration (Months)">
                      <InputNumber
                        style={{ width: "100%" }}
                        placeholder="e.g. 3"
                        addonAfter="Months"
                        min={1}
                        max={60}
                        size="large"
                      />
                    </Form.Item>
                  </Col>
                  <Col span={8}>
                    <Form.Item
                      name="budgetAllocated"
                      label="Budget (INR)"
                      rules={[{ required: true, message: "Please enter the budget" }]}
                      extra={<span style={{ color: '#666', fontSize: '12px' }}>This contributes to the total engagement budget.</span>}
                    >
                      <InputNumber
                        style={{ width: "100%" }}
                        placeholder="Enter budget in INR"
                        formatter={(value) => `₹ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
                        parser={(value) => value.replace(/₹\s?|(,*)/g, "")}
                        size="large"
                        min={0}
                      />
                    </Form.Item>
                  </Col>
                </Row>
              </div>
            )}

            {creationMode === "STAFF_AUGMENTATION" && currentStep === 1 && (
              <div className="step-content">
                <div className="form-section-title">Team & Resource Requirements</div>

                <Row gutter={24}>
                  <Col span={12}>
                    <Form.Item name="expectedTeamSize" label="Number of Resources Needed">
                      <InputNumber
                        min={1}
                        max={100}
                        placeholder="How many resources do you need?"
                        style={{ width: "100%" }}
                        size="large"
                      />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item name="experienceLevel" label="Experience Level Required">
                      <Select placeholder="Select experience level" size="large" allowClear>
                        <Option value="Junior">Junior (0-2 years)</Option>
                        <Option value="Mid">Mid-Level (2-5 years)</Option>
                        <Option value="Senior">Senior (5-8 years)</Option>
                        <Option value="Lead">Lead/Principal (8+ years)</Option>
                        <Option value="Mixed">Mixed (any level)</Option>
                      </Select>
                    </Form.Item>
                  </Col>
                </Row>

                <Form.Item name="criticalRoles" label="Required Roles / Skills">
                  <Select mode="multiple" placeholder="Select the roles you need" size="large" allowClear>
                    {ROLE_OPTIONS.map((role) => (
                      <Option key={role} value={role}>{role}</Option>
                    ))}
                  </Select>
                </Form.Item>

                <Form.Item name="country" label="Country">
                  <Select
                    placeholder="Select country"
                    size="large"
                    allowClear
                    showSearch
                    optionFilterProp="children"
                    onChange={(val) => {
                      fetchCities(val || null);
                      form.setFieldsValue({ preferredLocations: [] });
                    }}
                  >
                    {countries.map((c) => (
                      <Option key={c.value} value={c.value}>{c.label}</Option>
                    ))}
                  </Select>
                </Form.Item>

                <Form.Item name="preferredLocations" label="Preferred Work Location">
                  <Select mode="multiple" placeholder="Select country first, then preferred cities" size="large" allowClear showSearch optionFilterProp="children">
                    {cities.map((c) => (
                      <Option key={c.value} value={c.value}>{c.label}</Option>
                    ))}
                  </Select>
                </Form.Item>

                <div className="form-section-title" style={{ marginTop: 32 }}>Request Summary</div>

                <div className="summary-card">
                  <Row gutter={[16, 16]}>
                    <Col span={12}>
                      <div className="summary-row">
                        <div className="summary-label">Request Title</div>
                        <div className="summary-value">{form.getFieldValue("title") || "Not specified"}</div>
                      </div>
                    </Col>
                    <Col span={12}>
                      <div className="summary-row">
                        <div className="summary-label">Resources Needed</div>
                        <div className="summary-value">{form.getFieldValue("expectedTeamSize") || "Not specified"}</div>
                      </div>
                    </Col>
                    <Col span={24}>
                      <div className="summary-row">
                        <div className="summary-label">Required Roles</div>
                        <div>
                          {(form.getFieldValue("criticalRoles") || []).length > 0 ? (
                            form.getFieldValue("criticalRoles").map((role) => (
                              <Tag key={role} color="blue" style={{ marginBottom: 4 }}>{role}</Tag>
                            ))
                          ) : (
                            <span style={{ color: "#94a3b8" }}>No roles selected</span>
                          )}
                        </div>
                      </div>
                    </Col>
                  </Row>
                </div>
              </div>
            )}

            {creationMode !== "STAFF_AUGMENTATION" && currentStep === 0 && (
              <div className="step-content">
                <Form.Item
                  name="engagementId"
                  label="Select Engagement"
                  rules={[{ required: true, message: "Please select an engagement" }]}
                >
                  <Select 
                    placeholder="Choose the engagement for this project"
                    disabled={isEdit || !!preselectedEngagementId}
                    size="large"
                    onChange={(value) => {
                      const eng = activeEngagements.find(e => e.id === value);
                      setSelectedEngagement(eng);
                      form.setFieldsValue({ budgetAllocated: undefined });
                    }}
                  >
                    {activeEngagements.map((eng) => (
                      <Option key={eng.id} value={eng.id}>
                        {eng.title} ({eng.id})
                      </Option>
                    ))}
                  </Select>
                </Form.Item>

                <Form.Item
                  name="title"
                  label="Project Name"
                  rules={[{ required: true, message: "Please enter project name" }]}
                >
                  <Input placeholder="e.g., E-Commerce Platform Rebuild" size="large" />
                </Form.Item>

                <Form.Item
                  name="projectObjective"
                  label="Requirement Description"
                  rules={[{ required: true, message: "Please describe the requirement" }]}
                >
                  <TextArea 
                    rows={4} 
                    placeholder="Describe the project requirements, scope, and goals. What problem are you trying to solve?"
                    size="large"
                  />
                </Form.Item>

                <Row gutter={24}>
                  <Col span={12}>
                    <Form.Item
                      name="timeline"
                      label="Expected Timeline"
                    >
                      <RangePicker 
                        style={{ width: "100%" }}
                        size="large"
                        placeholder={["Start Date", "End Date"]}
                      />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item
                      name="deliveryType"
                      label="Delivery Type"
                      rules={[{ required: true, message: "Please select delivery type" }]}
                    >
                      <Select placeholder="What type of work is this?" size="large">
                        <Option value="build">Build - New Development</Option>
                        <Option value="enhance">Enhance - Improve Existing</Option>
                        <Option value="migrate">Migrate - Platform Migration</Option>
                      </Select>
                    </Form.Item>
                  </Col>
                </Row>

                <Form.Item
                  name="budgetAllocated"
                  label="Project Budget (INR)"
                  rules={[
                    { required: true, message: "Please enter the project budget" },
                  ]}
                  extra={
                    <span style={{ color: '#666', fontSize: '12px' }}>
                      This budget contributes to the total engagement budget and is used to track your spending. It will not affect individual talent profile rates.
                    </span>
                  }
                >
                  <InputNumber
                    style={{ width: "100%" }}
                    placeholder="Enter project budget in INR"
                    formatter={(value) => `₹ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
                    parser={(value) => value.replace(/₹\s?|(,*)/g, "")}
                    size="large"
                    min={0}
                  />
                </Form.Item>
              </div>
            )}

            {currentStep === 1 && (
              <div className="step-content">
                <div className="form-section-title">Select Your Technology Stack</div>
                
                <Form.Item
                  name="primaryTechnologies"
                  label="Primary Technologies"
                  rules={[
                    { required: true, message: "Please select at least one primary technology" },
                    ...(creationMode === "STAFF_AUGMENTATION" ? [{ type: "array", max: 3, message: "You can select up to 3 primary technologies for Staff Augmentation" }] : []),
                  ]}
                  extra={
                    creationMode === "STAFF_AUGMENTATION" ? (
                      <span style={{ color: primaryTechCount >= 3 ? "#cf1322" : "#888", fontSize: 12, fontWeight: primaryTechCount >= 3 ? 600 : 400 }}>
                        {primaryTechCount} of 3 selected{primaryTechCount >= 3 ? " — limit reached" : ""}
                      </span>
                    ) : null
                  }
                >
                  <Select
                    mode="multiple"
                    placeholder={creationMode === "STAFF_AUGMENTATION" ? "Select the main technologies (max 3)" : "Select the main technologies for this project"}
                    size="large"
                    allowClear
                    showSearch
                    onChange={(val) => {
                      if (creationMode === "STAFF_AUGMENTATION" && val.length > 3) {
                        form.setFieldsValue({ primaryTechnologies: val.slice(0, 3) });
                        message.warning("Staff Augmentation allows up to 3 primary technologies");
                      }
                    }}
                  >
                    {TECHNOLOGY_OPTIONS.map((tech) => (
                      <Option
                        key={tech}
                        value={tech}
                        disabled={creationMode === "STAFF_AUGMENTATION" && primaryTechCount >= 3 && !(watchedPrimaryTech || []).includes(tech)}
                      >{tech}</Option>
                    ))}
                  </Select>
                </Form.Item>

                <div className="custom-tech-input">
                  <Input
                    placeholder={creationMode === "STAFF_AUGMENTATION" && primaryTechCount >= 3 ? "Limit reached — deselect one to add a custom technology" : "Add a custom technology not listed above"}
                    value={customTech}
                    onChange={(e) => setCustomTech(e.target.value)}
                    onPressEnter={addCustomTechnology}
                    size="large"
                    disabled={creationMode === "STAFF_AUGMENTATION" && primaryTechCount >= 3}
                  />
                  <Button 
                    onClick={addCustomTechnology} 
                    icon={<PlusOutlined />}
                    className="add-tech-btn"
                    size="large"
                    disabled={creationMode === "STAFF_AUGMENTATION" && primaryTechCount >= 3}
                  >
                    Add
                  </Button>
                </div>

                <Form.Item
                  name="secondaryTechnologies"
                  label="Secondary Technologies (Optional)"
                  style={{ marginTop: 24 }}
                >
                  <Select
                    mode="multiple"
                    placeholder="Select supporting technologies"
                    size="large"
                    allowClear
                    showSearch
                  >
                    {TECHNOLOGY_OPTIONS.map((tech) => (
                      <Option key={tech} value={tech}>{tech}</Option>
                    ))}
                  </Select>
                </Form.Item>

                <div className="form-section-title" style={{ marginTop: 32 }}>Architecture Preferences</div>

                <Row gutter={24}>
                  <Col span={12}>
                    <Form.Item
                      name="architectureType"
                      label="Architecture Type"
                      rules={[{ required: true, message: "Please select architecture type" }]}
                    >
                      <Select placeholder="Choose architecture style" size="large">
                        <Option value="monolith">Monolith - Single Unified Codebase</Option>
                        <Option value="microservices">Microservices - Distributed Services</Option>
                        <Option value="hybrid">Hybrid - Mix of Both</Option>
                      </Select>
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item
                      name="integrationComplexity"
                      label="Integration Complexity"
                      rules={[{ required: true, message: "Please select integration complexity" }]}
                    >
                      <Select placeholder="How many integrations?" size="large">
                        <Option value="low">Low - Minimal External Integrations</Option>
                        <Option value="medium">Medium - Some Third-party APIs</Option>
                        <Option value="high">High - Complex Integration Requirements</Option>
                      </Select>
                    </Form.Item>
                  </Col>
                </Row>
              </div>
            )}

            {currentStep === 2 && (
              <div className="step-content">
                <div className="form-section-title">Project Complexity Assessment</div>

                <Form.Item
                  name="complexityLevel"
                  label="Overall Project Complexity"
                  rules={[{ required: true }]}
                >
                  <div className="slider-wrapper">
                    <div className="slider-value">
                      {complexityMarks[complexityLevel] || "Medium"}
                    </div>
                    <div className="slider-labels">
                      <span>Simple</span>
                      <span>Complex</span>
                    </div>
                    <Slider
                      value={complexityLevel}
                      marks={complexityMarks}
                      step={25}
                      tooltip={{ formatter: (value) => complexityMarks[value] || `${value}%` }}
                      onChange={(value) => {
                        setComplexityLevel(value);
                        form.setFieldsValue({ complexityLevel: value });
                      }}
                    />
                  </div>
                </Form.Item>

                <Form.Item
                  name="maturityIndex"
                  label="Project Maturity Index"
                  rules={[{ required: true }]}
                  style={{ marginTop: 32 }}
                >
                  <div className="slider-wrapper">
                    <div className="slider-value">
                      Level {maturityIndex}
                    </div>
                    <div className="slider-labels">
                      <span>Initial</span>
                      <span>Optimizing</span>
                    </div>
                    <Slider
                      value={maturityIndex}
                      min={1}
                      max={5}
                      marks={maturityMarks}
                      step={1}
                      onChange={(value) => {
                        setMaturityIndex(value);
                        form.setFieldsValue({ maturityIndex: value });
                      }}
                    />
                  </div>
                </Form.Item>

                <div className="form-section-title" style={{ marginTop: 32 }}>Security Considerations</div>

                <Form.Item
                  name="dataSensitivity"
                  label="Does this project handle sensitive data or require special security?"
                  valuePropName="checked"
                >
                  <div className="option-cards">
                    <div 
                      className={`option-card ${!dataSensitivity ? "selected" : ""}`}
                      onClick={() => {
                        setDataSensitivity(false);
                        form.setFieldsValue({ dataSensitivity: false });
                      }}
                    >
                      <div className="option-icon">🔓</div>
                      <div className="option-label">No Special Requirements</div>
                    </div>
                    <div 
                      className={`option-card ${dataSensitivity ? "selected" : ""}`}
                      onClick={() => {
                        setDataSensitivity(true);
                        form.setFieldsValue({ dataSensitivity: true });
                      }}
                    >
                      <div className="option-icon">🔐</div>
                      <div className="option-label">Yes, Security Sensitive</div>
                    </div>
                  </div>
                </Form.Item>
              </div>
            )}

            {currentStep === 3 && (
              <div className="step-content">
                <div className="form-section-title">Team Requirements</div>

                <Form.Item
                  name="expectedTeamSize"
                  label="Expected Team Size"
                >
                  <InputNumber
                    min={1}
                    max={100}
                    placeholder="How many team members do you need?"
                    style={{ width: "100%" }}
                    size="large"
                  />
                </Form.Item>

                <Form.Item
                  name="criticalRoles"
                  label="Critical Roles Needed"
                >
                  <Select
                    mode="multiple"
                    placeholder="Select the key roles required for this project"
                    size="large"
                    allowClear
                  >
                    {ROLE_OPTIONS.map((role) => (
                      <Option key={role} value={role}>{role}</Option>
                    ))}
                  </Select>
                </Form.Item>

                <div className="form-section-title" style={{ marginTop: 32 }}>Project Summary</div>

                <div className="summary-card">
                  <Row gutter={[16, 16]}>
                    <Col span={12}>
                      <div className="summary-row">
                        <div className="summary-label">Project Name</div>
                        <div className="summary-value">
                          {form.getFieldValue("title") || "Not specified"}
                        </div>
                      </div>
                    </Col>
                    <Col span={12}>
                      <div className="summary-row">
                        <div className="summary-label">Delivery Type</div>
                        <div className="summary-value">
                          {form.getFieldValue("deliveryType") || "Not specified"}
                        </div>
                      </div>
                    </Col>
                    <Col span={24}>
                      <div className="summary-row">
                        <div className="summary-label">Primary Technologies</div>
                        <div>
                          {(form.getFieldValue("primaryTechnologies") || []).length > 0 ? (
                            form.getFieldValue("primaryTechnologies").map((tech) => (
                              <Tag key={tech} color="blue" style={{ marginBottom: 4 }}>{tech}</Tag>
                            ))
                          ) : (
                            <span style={{ color: "#94a3b8" }}>No technologies selected</span>
                          )}
                        </div>
                      </div>
                    </Col>
                    <Col span={12}>
                      <div className="summary-row">
                        <div className="summary-label">Architecture</div>
                        <div className="summary-value">
                          {form.getFieldValue("architectureType") || "Not specified"}
                        </div>
                      </div>
                    </Col>
                    <Col span={12}>
                      <div className="summary-row">
                        <div className="summary-label">Complexity</div>
                        <div className="summary-value">
                          {complexityMarks[complexityLevel] || "Medium"}
                        </div>
                      </div>
                    </Col>
                  </Row>
                </div>
              </div>
            )}
          </Form>
        </div>

        <div className="card-footer">
          <Button 
            className="cancel-btn"
            onClick={() => navigate("/customer/projects")}
          >
            Cancel
          </Button>
          <div className="nav-buttons">
            {currentStep > 0 && (
              <Button 
                icon={<LeftOutlined />} 
                onClick={handlePrev}
                className="back-btn"
              >
                Back
              </Button>
            )}
            {currentStep < activeSteps.length - 1 ? (
              <Button 
                type="primary" 
                onClick={handleNext}
                className="next-btn"
              >
                Next <RightOutlined />
              </Button>
            ) : (
              <Button 
                type="primary" 
                onClick={handleSubmit} 
                loading={loading}
                className="submit-btn"
              >
                {isEdit ? "Update Project" : "Submit for Approval"}
              </Button>
            )}
          </div>
        </div>
      </div>
    </FormWrapper>
  );
};

export default CreateProject;
